import React, { useState } from "react";

const ProjectIncome = ({
  projekt,
  budget,
  drittkosten,
  bezahlt,
  payments,
  maxValue,
}) => {
  const [showPayments, setShowPayments] = useState(false);

  // Scale based on global maxValue (highest of budget or bezahlt across all projects)
  const scale = maxValue || budget || 1;

  // Budget bar calculations
  // Total width = budget, split into drittkosten (red) + eigenanteil (green)
  const totalWidthPercent = (budget / scale) * 100;
  const eigenanteilBudget = budget - (drittkosten || 0);
  const drittkostenWidth = budget > 0 ? ((drittkosten || 0) / budget) * 100 : 0;
  const eigenanteilBudgetWidth = budget > 0 ? (eigenanteilBudget / budget) * 100 : 0;

  // Calculate income and expenses from payments
  // Bezahlt = total income payments (what customers paid us)
  const bezahltIncome = payments
    .filter((p) => p.typ === "Einnahme")
    .reduce((sum, p) => sum + p.betrag, 0);
  // Ausgaben = total expense payments (what we paid for subcontractors/costs)
  const ausgaben = payments
    .filter((p) => p.typ === "Ausgabe")
    .reduce((sum, p) => sum + p.betrag, 0);
  // Einnahmen = Bezahlt - Ausgaben (net income)
  const einnahmen = bezahltIncome - ausgaben;

  // Paid bar: red (expenses) + green (income), side by side
  // Total width of paid bar is based on bezahltIncome (income received)
  const totalPaidBarValue = bezahltIncome + ausgaben;
  const totalPaidWidthPercent = (totalPaidBarValue / scale) * 100;
  const expenseWidthInPaid = totalPaidBarValue > 0 ? (ausgaben / totalPaidBarValue) * 100 : 0;
  const incomeWidthInPaid = totalPaidBarValue > 0 ? (bezahltIncome / totalPaidBarValue) * 100 : 0;

  const formatCHF = (amount) => {
    return new Intl.NumberFormat("de-CH", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  const togglePayments = () => {
    setShowPayments(!showPayments);
  };

  return (
    <div className="project-income">
      <div className="project-income-header">
        <span className="project-code">{projekt.code}</span>
        <span className="project-name">{projekt.name}</span>
        <div className="project-values">
          <div className="values-row">
            <span className="total-value">CHF {formatCHF(budget)}</span>
            <span className="separator">/</span>
            <span className="honorar-value">CHF {formatCHF(eigenanteilBudget)}</span>
          </div>
          <div className="values-row secondary">
            <span className="total-value">CHF {formatCHF(bezahltIncome)}</span>
            <span className="separator">/</span>
            <span className="bezahlt-value">CHF {formatCHF(einnahmen)}</span>
          </div>
        </div>
      </div>

      <div className="project-bars">
        {/* Budget bar: red (Drittkosten) + green (Eigenanteil) */}
        <div
          className="project-bar"
          title={`Budget: CHF ${formatCHF(budget)}\nDrittkosten: CHF ${formatCHF(drittkosten)}\nEigenanteil: CHF ${formatCHF(eigenanteilBudget)}`}
        >
          <div className="bar-inner" style={{ width: `${totalWidthPercent}%` }}>
            <div className="bar-segment drittkosten" style={{ width: `${drittkostenWidth}%` }} />
            <div className="bar-segment honorar" style={{ width: `${eigenanteilBudgetWidth}%` }} />
          </div>
        </div>

        {/* Paid bar: red (expenses) + green (income) side by side - clickable */}
        <div
          className={`project-bar clickable ${showPayments ? "active" : ""}`}
          title={`Bezahlt: CHF ${formatCHF(bezahltIncome)}\nAusgaben: CHF ${formatCHF(ausgaben)}\nEinnahmen: CHF ${formatCHF(einnahmen)}\nKlicken für Details`}
          onClick={togglePayments}
        >
          <div className="bar-inner" style={{ width: `${totalPaidWidthPercent}%` }}>
            <div className="bar-segment expense" style={{ width: `${expenseWidthInPaid}%` }} />
            <div className="bar-segment income" style={{ width: `${incomeWidthInPaid}%` }} />
          </div>
        </div>
      </div>

      {/* Payments list - shown when clicked */}
      {showPayments && payments && payments.length > 0 && (
        <div className="payments-list">
          {payments
            .sort((a, b) => new Date(b.datum) - new Date(a.datum))
            .map((payment, index) => (
              <div
                key={payment.id || index}
                className={`payment-row ${payment.typ === "Einnahme" ? "income" : "expense"}`}
              >
                <span className="payment-date">{formatDate(payment.datum)}</span>
                <span className="payment-description">{payment.beschreibung}</span>
                <span className="payment-amount">
                  {payment.typ === "Einnahme" ? "+" : "-"}CHF {formatCHF(payment.betrag)}
                </span>
              </div>
            ))}
        </div>
      )}

      {showPayments && (!payments || payments.length === 0) && (
        <div className="payments-list empty">
          Keine Zahlungen vorhanden
        </div>
      )}
    </div>
  );
};

export default ProjectIncome;
